import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import qs from 'query-string'

import { useTranslation } from '../../contexts/I18nContext'
import {
  componentLoader,
  formatCoversPerUnit,
  pluralize,
  readCookie,
} from '../../helpers'
import RecipePoster from '../../components/RecipePoster'
import CoversCountSelector from '../../components/CoversCountSelector'
import RecipeNutritionalFacts from '../../components/RecipeNutritionalFacts'
import RecipePricePerPortion from '../../components/RecipePricePerPortion'
import { COLORS, getColorBasedBackgroundColor, THEME } from '../../styles'
import RecipeCookingSupporting from '../../components/RecipeCookingSupporting'
import RecipeVideoPlayer from '../../components/RecipeVideoPlayer'
import { COOKIES } from '../../constants'

import Styled from './RecipeLayout.styled'

const MinimalistFooter = dynamic(
  () =>
    componentLoader(
      () =>
        import(
          /* webpackChunkName: "@components/MinimalistFooterRecipe" */ '../../../src/components/MinimalistFooter'
        ),
    ),
  { ssr: false },
)
const OpenInAppBanner = dynamic(
  () =>
    componentLoader(
      () =>
        import(
          /* webpackChunkName: "@components/OpenRecipeInAppBannerRecipe" */ '../../../src/components/OpenRecipeInAppBanner'
        ),
    ),
  { ssr: false },
)

const RecipeLayout = ({
  auth,
  className,
  config,
  editMenu,
  favorRecipe,
  hideFeedbacks,
  isProfilingRecipe,
  isRecipeAdded,
  oldRecipeId,
  onGoBack,
  openModal,
  preview,
  profile,
  profiledRecipe,
  recipe,
  setProfiledRecipe,
  showError,
  similarRecipes,
  titleEdito,
}) => {
  const { t } = useTranslation()
  const router = useRouter()

  const preparationTimeUpdated = useMemo(() => {
    return (
      (recipe.preparationTime || 0) +
      (recipe.preparationExtraTimePerCover || 0) * recipe.coversCount
    )
  }, [
    recipe.preparationTime,
    recipe.preparationExtraTimePerCover,
    recipe.coversCount,
  ])

  const [hideCalories, setHideCalories] = useState(false)
  useEffect(() => {
    setHideCalories(readCookie(COOKIES.HIDE_CALORIES.name) === '1')
  }, [])
  const calories = useMemo(() => {
    return (
      recipe.nutritionalFacts &&
      recipe.nutritionalFacts.length &&
      recipe.nutritionalFacts.find(({ id }) => id === 'ENERC')
    )
  }, [recipe.nutritionalFacts])

  const backgroundColor = useMemo(() => {
    return (
      recipe?.backgroundPattern?.color ||
      recipe?.backgroundColor ||
      COLORS.cream
    )
  }, [recipe])
  const backgroundPatternUrl = useMemo(() => {
    return (
      recipe?.backgroundPattern?.imageUrlUHD ||
      recipe?.backgroundPattern?.imageUrlHD ||
      recipe?.backgroundPattern?.imageUrl
    )
  }, [recipe])
  const textColor = useMemo(
    () =>
      getColorBasedBackgroundColor(backgroundColor, COLORS.black, COLORS.white),
    [backgroundColor],
  )

  const [isVideoPlayed, setIsVideoPlayed] = useState(false)
  const handleVideoPlayer = useCallback(() => {
    setIsVideoPlayed(true)
  }, [])

  return (
    <Styled className={className}>
      <Styled.side preview={preview} key={`recipe-description-${recipe.id}`}>
        <Styled.scrollView>
          <Styled.header preview={preview}>
            <Styled.heading
              aggregateRating={recipe.aggregateRating}
              author={recipe?.author}
              description={recipe.description}
              onGoBack={onGoBack}
              title={recipe.title}
              totalFeedbacks={recipe.totalFeedbacks}
              hideFeedbacks={hideFeedbacks}
              isVideoPlayed={isVideoPlayed}
            />

            {preview ? null : (
              <>
                <OpenInAppBanner recipeId={recipe.id} source="jow" />
                <Styled.actions
                  accessToken={auth.accessToken}
                  editMenu={editMenu}
                  favorRecipe={favorRecipe}
                  favoriteRecipes={profile.favoriteRecipes}
                  isLoading={isProfilingRecipe}
                  isRecipeAdded={isRecipeAdded}
                  oldRecipeId={oldRecipeId}
                  openModal={openModal}
                  recipe={profiledRecipe}
                  title={titleEdito}
                  displayMenuEdition
                />
              </>
            )}

            {recipe?.tagsEdito?.length > 0 ? (
              <Styled.tags backgroundColor={backgroundColor}>
                {recipe.tagsEdito.map((tag) => (
                  <Styled.tag key={`tag-${tag}`}>{tag}</Styled.tag>
                ))}
              </Styled.tags>
            ) : null}

            <Styled.characteristicsIndicators
              backgroundColor={backgroundColor}
              calories={hideCalories ? undefined : calories}
              cookingTime={recipe.cookingTime}
              restingTime={recipe.restingTime}
              preparationTime={preparationTimeUpdated}
              textColor={textColor}
            />
          </Styled.header>

          <Styled.details>
            <Styled.section id="ingredients">
              <Styled.subtitle hasPicker>
                {`${t('RECIPE.INGREDIENTS')} ${formatCoversPerUnit({
                  unitName: recipe.unitName,
                  roundedCoversCount: recipe.roundedCoversCount,
                  coversCount: recipe.coversCount,
                  prefix: `${t('FOR')} `,
                })}`}
              </Styled.subtitle>
              <Styled.covers>
                <CoversCountSelector
                  setCoversCount={(value) => {
                    if (router.pathname !== '/preview/recipe') {
                      router.push(
                        `/recipes/${router.query.recipeId}?${qs.stringify(
                          { ...router.query, recipeId: '', coversCount: value },
                          { skipEmptyString: true, skipNull: true },
                        )}`,
                        undefined,
                        { shallow: true },
                      )
                    }
                    if (setProfiledRecipe) {
                      setProfiledRecipe((r) => ({ ...r, coversCount: value }))
                    }
                  }}
                  coversCount={recipe.coversCount}
                  options={recipe.coversOptions}
                />
              </Styled.covers>
              <Styled.constituents
                constituents={recipe.constituents}
                additionalConstituents={recipe.additionalConstituents}
                coversCount={recipe.coversCount}
                showError={showError}
                withIngredientLinks
              />
            </Styled.section>

            {recipe.requiredTools && recipe.requiredTools.length > 0 && (
              <Styled.section id="utensils">
                <Styled.subtitle>
                  {pluralize(t('UTENSIL'), recipe.requiredTools.length, {
                    inclusive: false,
                  })}
                </Styled.subtitle>
                <Styled.tools>
                  {recipe.requiredTools.map((tool) => tool.name).join(', ')}
                </Styled.tools>
              </Styled.section>
            )}

            {(recipe.directions || []).length ? (
              <Styled.section id="instructions">
                <Styled.subtitle>{t('RECIPE.RECIPE')}</Styled.subtitle>
                <Styled.directions directions={recipe.directions} />
                {recipe?.tip?.description ? (
                  <Styled.tipCard tip={recipe.tip} />
                ) : null}
              </Styled.section>
            ) : null}

            {recipe.nutritionalFacts && recipe.nutritionalFacts.length ? (
              <Styled.section id="nutritional-facts" data-scroll-offset>
                <RecipeNutritionalFacts
                  nutritionalFacts={recipe.nutritionalFacts}
                  title={recipe.title}
                />
              </Styled.section>
            ) : null}

            {recipe.pricePerPortionTag &&
            config?.recipePricePerPortionLevels?.length ? (
              <Styled.section id="price-per-portion" data-scroll-offset>
                <RecipePricePerPortion
                  pricePerPortionTag={recipe.pricePerPortionTag}
                  recipePricePerPortionLevels={
                    config.recipePricePerPortionLevels
                  }
                />
              </Styled.section>
            ) : null}

            {hideFeedbacks ? null : (
              <Styled.recipeFeedbackSection
                aggregateRating={recipe.aggregateRating}
                readOnly={preview}
                recipeAuthor={recipe?.author}
                recipeId={recipe.id}
                recipeImageUrl={recipe.imageUrl}
                recipeSource={recipe.source}
                recipeTitle={recipe.title}
                totalFeedbacks={recipe.totalFeedbacks}
              />
            )}

            {!preview && similarRecipes && similarRecipes.length ? (
              <Styled.section
                id="similar-recipes"
                backgroundColor={THEME.COLORS.background.primary}
                noBorder
              >
                <Styled.subtitle hasCaption>
                  {t('RECIPE.SIMILAR_RECIPES')}
                </Styled.subtitle>
                <Styled.similarRecipeList
                  variant="recipe-card"
                  gap={12}
                  autoFill
                >
                  {similarRecipes.map((similarRecipe) => (
                    <Styled.similarRecipeListItem key={similarRecipe.id}>
                      <Styled.recipeCard
                        imageUrl={similarRecipe.imageUrl}
                        coversCount={similarRecipe.coversCount}
                        id={similarRecipe.id}
                        isLoading={false}
                        title={similarRecipe.title}
                        description={similarRecipe.composition}
                        slug={similarRecipe.slug}
                        source={similarRecipe.source}
                        roundedCoversCount={similarRecipe.roundedCoversCount}
                        unitName={similarRecipe.unitName}
                        tagsEdito={similarRecipe.tagsEdito}
                        missingIngredients={similarRecipe.missingIngredients}
                        showError={showError}
                        openModal={openModal}
                        prefetch={false}
                      >
                        <RecipeCookingSupporting
                          id={similarRecipe.id}
                          title={similarRecipe.title}
                          favorites={profile.favoriteRecipes}
                          slug={similarRecipe.slug}
                          coversCount={similarRecipe.coversCount}
                          source={recipe.source}
                        />
                      </Styled.recipeCard>
                    </Styled.similarRecipeListItem>
                  ))}
                </Styled.similarRecipeList>
              </Styled.section>
            ) : null}

            {!preview && <MinimalistFooter />}
          </Styled.details>
        </Styled.scrollView>
      </Styled.side>

      <Styled.mediaContainer
        preview={preview}
        key={`recipe-media-${recipe.id}`}
        backgroundColor={backgroundColor}
      >
        {recipe.videoUrl && recipe.directions ? (
          <RecipeVideoPlayer
            backgroundColor={backgroundColor}
            backgroundPatternUrl={backgroundPatternUrl}
            constituents={recipe.constituents}
            additionalConstituents={recipe.additionalConstituents}
            coversCount={recipe.coversCount}
            difficulty={recipe.difficulty}
            directions={recipe.directions}
            id={recipe.id}
            imageUrl={
              recipe.imageUrlUHD || recipe.imageUrlHD || recipe.imageUrl
            }
            imageUrlHD={recipe.imageUrlHD}
            posterUrl={recipe.posterUrl}
            pricePerPortionTag={recipe.pricePerPortionTag}
            textColor={textColor}
            title={recipe.title}
            videoUrl={recipe.videoUrl}
            onVideoPlayed={handleVideoPlayer}
          />
        ) : (
          <RecipePoster
            backgroundColor={backgroundColor}
            backgroundPatternUrl={backgroundPatternUrl}
            difficulty={recipe.difficulty}
            imageUrl={
              recipe.imageUrlUHD || recipe.imageUrlHD || recipe.imageUrl
            }
            pricePerPortionTag={recipe.pricePerPortionTag}
            textColor={textColor}
            title={recipe.title}
          />
        )}
      </Styled.mediaContainer>
    </Styled>
  )
}

Styled.defaultProps = {
  config: {},
  slugs: {},
  auth: {},
  menu: {},
}

export default RecipeLayout
